import {chakra} from '@chakra-ui/react'
import {HeaderContext} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {ComponentProps, type KeyboardEvent} from 'react'
import {LuArrowDownUp, LuArrowDownZA, LuArrowUpAZ} from 'react-icons/lu'
import {Tooltip} from './tooltip'

interface Props<T> extends HeaderContext<T, unknown>, ComponentProps<typeof chakra.div> {}

/** A sortable column header: the whole header sorts, so it is not just the arrow that reacts (CY-180). */
export function SortableTableHeader<T extends {id: string}>({children, column, ...props}: Props<T>) {
  const {t} = useTranslate('common')
  const sorted = column.getIsSorted()
  const canSort = column.getCanSort()

  // first click ascending, second descending, third ascending again: never cleared, and never descending first
  // whatever the column's values (CY-357)
  function toggleSort() {
    column.toggleSorting(sorted === 'asc')
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    toggleSort()
  }

  return (
    <chakra.div
      {...props}
      display="flex"
      alignItems="center"
      gapX="2"
      width="100%"
      userSelect="none"
      cursor={canSort ? 'pointer' : undefined}
      role={canSort ? 'button' : undefined}
      tabIndex={canSort ? 0 : undefined}
      onClick={canSort ? toggleSort : undefined}
      onKeyDown={canSort ? onKeyDown : undefined}
    >
      {children}

      {canSort && (
        <Tooltip content={t('sort_column')}>
          <chakra.span display="inline-flex" flexShrink={0} color={sorted ? 'fg.muted' : 'fg.subtle'} aria-hidden>
            {sorted === 'asc' && <LuArrowUpAZ />}
            {sorted === 'desc' && <LuArrowDownZA />}
            {!sorted && <LuArrowDownUp />}
          </chakra.span>
        </Tooltip>
      )}
    </chakra.div>
  )
}
